import Joi from 'joi';
import { isPhoneNumberValid } from '../../../global/helpers/validators';
import { IRoundsRow } from '../../../swagger/gd/round';

// ─── TS Interface ────────────────────────────────────────────────────────────
export interface IRequestForm {
  // Interné polia (z fields.js / load response)
  status?: string;
  request_id?: string;
  gd_request_id?: string;
  expected_voucher_amount?: number;
  start?: string;
  startBy?: number;
  region_type_id?: string;
  round_text?: string;

  // IČO / DIČ / IČ DPH
  ico: string;
  dic: string;
  icdph: string;

  // Firemné údaje
  companyName: string;
  companyLegalForm: string;
  companyIBAN: string;
  companySWIFT?: string;
  companySize: string;
  skNace: string;
  companyStreet: string;
  companyCity: string;

  // Kontaktná osoba (povinná)
  contactPersonName: string;
  contactPersonPhone: string;
  contactPersonMail: string;

  // Splnomocnená osoba (voliteľná)
  authorizedPersonName?: string;
  authorizedPersonPhone?: string;
  authorizedPersonMail?: string;

  // Kolo výzvy — XsSearchSelect ukladá celý objekt (ako device select); '' pred výberom
  round_id: IRoundsRow | string;

  // Príspevok na energetický audit
  contributionForEnergyAudit: boolean;

  // Statutári (JSON string, napr. '[{"name":"..."}]')
  applicantChipForm: string;

  // Súhlas so spracovaním osobných údajov
  consent: boolean;

  // Platca DPH — checkbox vedľa IČ DPH
  vatPayer: boolean;

  // Krok 3 — Jediny podnik (3.1)
  singleEnterpriseNo: boolean;
  singleEnterpriseYes: boolean;

  // Krok 3 — Neoprávnené vrátenie pomoci (3.2)
  illegalAidReturn: boolean;

  // Krok 3 — Vylúčené oblasti a sektory (3.3, mutex pair)
  excludedSectorsNo: boolean;
  excludedSectorsYes: boolean;

  // Krok 3 — Iná minimálna pomoc (3.4, mutex pair)
  otherMinAidNo: boolean;
  otherMinAidYes: boolean;

  // Čestné vyhlásenie — potvrdenie podmienok podpory (krok 4)
  consentConditions: boolean;

  // Spôsob komunikácie (voliteľný — načítava sa z firmy)
  methodOfCommunication?: string;

  // Spôsob komunikácie — UI radio (writtenCommunication / electronicCommunication)
  communicationMethod?: string;

  // Vlastníci žiadateľa — len pre právnu formu družstvo/a.s.
  numberOfOwners?: number;
  // Pozn.: nie optional v TS type (kvôli RHF FieldArrayPath inferencii pre useFieldArray) — vždy '[]' v defaultValues
  owners: IOwnerForm[];
}

export interface IOwnerForm {
  type: 'legalEntity' | 'naturalPerson';
  name: string;
  rpoSearch: string;
  rpoName: string;
  ownershipInterest: string;
}

// ─── IBAN regex zo zp functions.js ───────────────────────────────────────────
const IBAN_REGEX = /^[a-zA-Z]{2}[0-9]{2}[a-zA-Z0-9]{4}[0-9]{7}([a-zA-Z0-9]?){0,16}$/;

// ─── IČ DPH regex (z fields.js) ──────────────────────────────────────────────
const ICDPH_REGEX = /^(?=[aA-zZ0-9]*$)(?:.{1}|.{12})$/;

// ─── Joi schema ───────────────────────────────────────────────────────────────
export const requestFormSchema = Joi.object<IRequestForm>({
  // Interné / read-only polia — voliteľné
  status: Joi.string().allow('').optional(),
  request_id: Joi.string().allow('').optional(),
  gd_request_id: Joi.string().allow('').optional(),
  expected_voucher_amount: Joi.number().optional(),
  start: Joi.string().allow('').optional(),
  startBy: Joi.number().optional(),
  region_type_id: Joi.string().allow('').optional(),
  round_text: Joi.string().allow('').optional(),

  // IČO: presne 8 znakov, povinné
  ico: Joi.string().length(8).required(),

  // DIČ: presne 10 číslic, povinné
  dic: Joi.string()
    .pattern(/^[0-9]{10}$/)
    .required(),

  // IČ DPH: regex, povinné
  icdph: Joi.string().pattern(ICDPH_REGEX).required(),

  // Firemné údaje — povinné
  companyName: Joi.string().required(),
  companyLegalForm: Joi.string().required(),
  companyIBAN: Joi.string().pattern(IBAN_REGEX).required(),
  companySize: Joi.string().required(),
  skNace: Joi.string().required(),
  companyStreet: Joi.string().required(),
  companyCity: Joi.string().required(),

  // SWIFT — voliteľné
  companySWIFT: Joi.string().allow('').optional(),

  // Kontaktná osoba — povinná
  contactPersonName: Joi.string().required(),
  contactPersonPhone: Joi.string()
    .custom((v, h) => (isPhoneNumberValid(v) ? v : h.error('any.invalid')))
    .required(),
  contactPersonMail: Joi.string()
    .email({ tlds: { allow: false } })
    .required(),

  // Splnomocnená osoba — voliteľná
  authorizedPersonName: Joi.string().allow('').optional(),
  authorizedPersonPhone: Joi.string().allow('').optional(),
  authorizedPersonMail: Joi.string()
    .email({ tlds: { allow: false } })
    .allow('')
    .optional(),

  // Kolo výzvy — react-select ukladá objekt; required = vybraný objekt s round_id
  round_id: Joi.any()
    .custom((v, h) => {
      const id = v && typeof v === 'object' ? (v as IRoundsRow).round_id : v;
      return id ? v : h.error('any.required');
    })
    .required(),

  // Príspevok na energetický audit — boolean, voliteľné (default false)
  contributionForEnergyAudit: Joi.boolean().default(false),

  // Statutári ako JSON string
  applicantChipForm: Joi.string().allow('').optional(),

  // Súhlas — zbiera sa v ConsentDialog (nie inline), preto tu nie je povinný
  consent: Joi.boolean().optional(),

  // Platca DPH
  vatPayer: Joi.boolean().optional(),

  // Krok 3 — Jediny podnik (3.1, mutex pair)
  singleEnterpriseNo: Joi.boolean().optional(),
  singleEnterpriseYes: Joi.boolean().optional(),

  // Krok 3 — Neoprávnené vrátenie pomoci (3.2)
  illegalAidReturn: Joi.boolean().optional(),

  // Krok 3 — Vylúčené oblasti a sektory (3.3, mutex pair)
  excludedSectorsNo: Joi.boolean().optional(),
  excludedSectorsYes: Joi.boolean().optional(),

  // Krok 3 — Iná minimálna pomoc (3.4, mutex pair)
  otherMinAidNo: Joi.boolean().optional(),
  otherMinAidYes: Joi.boolean().optional(),

  // Podmienky podpory — krok 4
  consentConditions: Joi.boolean().optional(),

  // Spôsob komunikácie — voliteľný
  methodOfCommunication: Joi.string().allow('').optional(),
  communicationMethod: Joi.string().valid('writtenCommunication', 'electronicCommunication').optional(),

  // Vlastníci žiadateľa — počet riadkov, povinné len keď je sekcia zobrazená (companyLegalForm druzstvo/as)
  numberOfOwners: Joi.number().integer().min(0).max(20).optional(),
  owners: Joi.array()
    .items(
      Joi.object({
        type: Joi.string().valid('legalEntity', 'naturalPerson').required(),
        name: Joi.when('type', {
          is: 'naturalPerson',
          then: Joi.string().required(),
          otherwise: Joi.string().allow('').optional()
        }),
        rpoSearch: Joi.when('type', {
          is: 'legalEntity',
          then: Joi.string().required(),
          otherwise: Joi.string().allow('').optional()
        }),
        rpoName: Joi.string().allow('').optional(),
        ownershipInterest: Joi.string()
          .custom((v, h) => {
            const n = Number(v);
            return Number.isFinite(n) && n >= 5 && n <= 100 ? v : h.error('any.invalid');
          })
          .required()
      })
    )
    .optional()
})
  // Podniky tvoriace jediný podnik so žiadateľom — dynamické polia rpo_search_companies_0, _1, ...
  // nie sú súčasťou modelu žiadosti (lokálny UI stav), preto sú tu len povolené a nikdy nepovinné.
  .pattern(/^rpo_search_companies_\d+$/, Joi.any().optional());
